import { useState, useEffect, useCallback } from 'react';

// Shown when login is blocked by the concurrent-session cap. The backend handed
// back a short-lived session-management token (props.token); we use it to list
// and remove the user's OWN devices, then let them continue signing in.
const apiBase = () =>
  window.location.hostname === 'localhost' ? 'http://localhost:7080/api' : '/api';

function SessionLimitModal({ token, username, maxSessions = 3, onClose, onResolved }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase()}/my-sessions`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(
          res.status === 401
            ? 'This window timed out. Please close it and sign in again.'
            : data.message || 'Could not load your devices.'
        );
        setSessions([]);
      } else {
        setSessions(data.sessions || []);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const removeOne = async (sessionId) => {
    setWorking(true);
    setError('');
    try {
      const res = await fetch(`${apiBase()}/my-sessions/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({ sessionId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) setError(data.message || 'Could not remove that device.');
      else await load();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const removeAll = async () => {
    setWorking(true);
    setError('');
    try {
      const res = await fetch(`${apiBase()}/my-sessions/revoke-all`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok || !data.success) setError(data.message || 'Could not remove devices.');
      else await load();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const hasRoom = sessions.length < maxSessions;
  const fmt = (d) => {
    if (!d) return 'unknown';
    try { return new Date(d).toLocaleString(); } catch { return 'unknown'; }
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <h2 style={styles.title}>Device limit reached</h2>
        <p style={styles.subtitle}>
          You’re signed in on {maxSessions} devices (the maximum). Remove one to continue
          signing in as <strong>{username}</strong>.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.muted}>Loading your devices…</div>
        ) : (
          <div style={styles.list}>
            {sessions.length === 0 && <div style={styles.muted}>No active devices.</div>}
            {sessions.map((s) => (
              <div key={s.sessionId} style={styles.item}>
                <div style={{ minWidth: 0 }}>
                  <div style={styles.deviceLine}>{s.platform} · {s.browser}</div>
                  <div style={styles.metaLine}>
                    {s.ip ? `IP ${s.ip}` : 'IP unknown'} · last active {fmt(s.lastSeen)}
                  </div>
                </div>
                <button
                  style={styles.removeBtn}
                  disabled={working}
                  onClick={() => removeOne(s.sessionId)}
                >
                  Sign out
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={styles.actions}>
          <button
            style={styles.secondaryBtn}
            disabled={working || sessions.length === 0}
            onClick={removeAll}
          >
            Sign out all
          </button>
          <div style={{ flex: 1 }} />
          <button style={styles.ghostBtn} disabled={working} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{ ...styles.primaryBtn, ...(hasRoom ? {} : styles.primaryBtnDisabled) }}
            disabled={working || !hasRoom}
            onClick={onResolved}
            title={hasRoom ? 'Continue signing in' : 'Remove a device first'}
          >
            Continue to login
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', color: '#1a1a1a', borderRadius: 12, width: '100%', maxWidth: 460,
    padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: 'inherit',
  },
  title: { margin: '0 0 6px', fontSize: 20, fontWeight: 700 },
  subtitle: { margin: '0 0 16px', fontSize: 14, color: '#555', lineHeight: 1.5 },
  error: {
    background: '#fdecec', color: '#b3261e', border: '1px solid #f5c2c0',
    borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12,
  },
  muted: { color: '#888', fontSize: 14, padding: '12px 0' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, maxHeight: 280, overflowY: 'auto' },
  item: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    border: '1px solid #e6e6e6', borderRadius: 8, padding: '10px 12px',
  },
  deviceLine: { fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  metaLine: { fontSize: 12, color: '#777', marginTop: 2 },
  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  removeBtn: {
    background: '#fff', color: '#b3261e', border: '1px solid #e6a9a6', borderRadius: 8,
    padding: '6px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  secondaryBtn: {
    background: '#fff', color: '#b3261e', border: '1px solid #e6a9a6', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, cursor: 'pointer',
  },
  ghostBtn: {
    background: 'transparent', color: '#555', border: '1px solid #ccc', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, cursor: 'pointer',
  },
  primaryBtn: {
    background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  primaryBtnDisabled: { background: '#a9c4f0', cursor: 'not-allowed' },
};

export default SessionLimitModal;
